import {
  ArrowRight,
  Check,
  Copy,
  Download,
  ExternalLink,
  Globe2,
  MonitorDown,
  ShieldCheck,
  X,
} from "lucide-react";
import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react";

import { useMessages } from "../../i18n";
import { LocaleQuickSwitch } from "../settings/LocaleQuickSwitch";
import { MAC_RELEASE } from "./macRelease";
import {
  attributedDownloadUrl,
  recordLandingVisitOnce,
} from "../operations/landingVisit";
import { startPageMessages } from "./startMessages";
import { recordDownloadIntent } from "../operations/webInteraction";
import "./start-page.css";

const APPLE_OPEN_ANYWAY_URL =
  "https://support.apple.com/guide/mac-help/open-a-mac-app-from-an-unknown-developer-mh40616/mac";
const LandingFarmPreview = lazy(async () => {
  const module = await import("./LandingFarmPreview");
  return { default: module.LandingFarmPreview };
});

function LandingFarmPreviewFallback() {
  const t = useMessages(startPageMessages);

  return (
    <div className="start-live-preview start-live-preview--loading start-menubar-demo">
      <div className="start-menubar-strip" aria-hidden="true">
        <span className="start-menubar-strip__app">{t("brand.name")}</span>
        <div className="start-menubar-strip__items">
          <span>⌁</span>
          <span>◐</span>
          <span className="start-menubar-tray__slime" />
          <time>9:41</time>
        </div>
      </div>
      <div className="start-menubar-popover">
        <div className="start-window-bar" aria-hidden="true">
          <p>
            <img src="/favicon.svg" alt="" />
            {t("preview.windowTitle")}
          </p>
        </div>
        <div className="start-farm">
          <p>{t("preview.loading")}</p>
        </div>
        <div className="start-preview-footer" aria-hidden="true">
          <p className="start-preview-footer__guide">
            <span />
            <span>
              <strong>{t("preview.loadingTitle")}</strong>
              <small>{t("preview.loadingDetail")}</small>
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

function LandingFarmPreviewSlot() {
  const hostRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(
    () =>
      typeof window === "undefined" ||
      !("IntersectionObserver" in window),
  );

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }
    if (!("IntersectionObserver" in window)) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { threshold: 0.01 },
    );
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={hostRef} className="start-preview-slot">
      {shouldLoad ? (
        <Suspense fallback={<LandingFarmPreviewFallback />}>
          <LandingFarmPreview />
        </Suspense>
      ) : (
        <LandingFarmPreviewFallback />
      )}
    </div>
  );
}

function isPublishedDownload(url: string): boolean {
  return /^https:\/\//.test(url);
}

export function StartPage() {
  const t = useMessages(startPageMessages);
  const [installOpen, setInstallOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const releaseReady = isPublishedDownload(MAC_RELEASE.downloadUrl);
  const releaseDownloadUrl = attributedDownloadUrl(
    MAC_RELEASE.downloadUrl,
    window.location.href,
  );
  const releaseChannel = MAC_RELEASE.notarized ? "STABLE" : "PREVIEW";

  useEffect(() => {
    void recordLandingVisitOnce();
  }, []);

  useEffect(() => {
    if (!installOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setInstallOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [installOpen]);

  const copyChecksum = async () => {
    if (!releaseReady) {
      return;
    }
    await navigator.clipboard.writeText(MAC_RELEASE.sha256);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_800);
  };

  const closeOnBackdrop = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      setInstallOpen(false);
    }
  };

  return (
    <main className="start-page">
      <header className="start-header">
        <a
          className="start-brand"
          href="/"
          aria-label={t("brand.homeLabel")}
        >
          <img src="/favicon.svg" alt="" />
          <span>
            <strong>{t("brand.name")}</strong>
            <small>{t("brand.tagline")}</small>
          </span>
        </a>
        <div className="start-header__tools">
          <LocaleQuickSwitch />
          <a className="start-header-link" href="/sample">
            {t("header.preview")}
            <ArrowRight aria-hidden="true" />
          </a>
        </div>
      </header>

      <section className="start-hero" aria-labelledby="start-title">
        <div className="start-copy">
          <p className="start-kicker">
            <span aria-hidden="true">●</span>
            {t("hero.kicker", { channel: releaseChannel })}
          </p>
          <h1
            id="start-title"
            aria-label={t("hero.titleLabel")}
          >
            <span>{t("hero.titleLine1")}</span>
            <span>
              <em>{t("hero.titleLine2")}</em>
            </span>
            <span>
              <em>{t("hero.titleLine3")}</em>
            </span>
          </h1>
          <p className="start-intro">
            <span>{t("hero.introLine1")}</span>
            <span>{t("hero.introLine2")}</span>
          </p>

          <div
            className="start-actions"
            aria-label={t("hero.actionsLabel")}
          >
            <button
              className="start-action start-action-primary"
              type="button"
              onClick={() => setInstallOpen(true)}
            >
              <MonitorDown aria-hidden="true" />
              <span>
                <strong>{t("hero.macCta")}</strong>
                <small>
                  {t("hero.macCtaDetail", {
                    architecture: MAC_RELEASE.architecture,
                  })}
                </small>
              </span>
              <ArrowRight aria-hidden="true" />
            </button>
            <a
              className="start-action start-action-secondary"
              href="/farm"
            >
              <Globe2 aria-hidden="true" />
              <span>
                <strong>{t("hero.webCta")}</strong>
                <small>{t("hero.webCtaDetail")}</small>
              </span>
              <ArrowRight aria-hidden="true" />
            </a>
          </div>

          <ul className="start-trust" aria-label={t("hero.trustLabel")}>
            <li>
              <ShieldCheck aria-hidden="true" />
              {t("hero.localFirst")}
            </li>
            <li>
              <Check aria-hidden="true" />
              {t("hero.noLogin")}
            </li>
            <li>
              <Check aria-hidden="true" />
              macOS 12+
            </li>
          </ul>
        </div>

        <div
          className="start-farm-stage"
          aria-label={t("hero.farmPreviewLabel")}
        >
          <LandingFarmPreviewSlot />
        </div>
      </section>

      <section
        className="start-release-strip"
        aria-label={t("release.regionLabel")}
      >
        <p>
          <strong>{t("release.eyebrow", { channel: releaseChannel })}</strong>
          <span>v{MAC_RELEASE.version}</span>
        </p>
        <dl>
          <div>
            <dt>{t("release.format")}</dt>
            <dd>{t("release.formatValue")}</dd>
          </div>
          <div>
            <dt>{t("release.support")}</dt>
            <dd>{MAC_RELEASE.architecture}</dd>
          </div>
          <div>
            <dt>{t("release.minimumVersion")}</dt>
            <dd>{MAC_RELEASE.minimumSystemVersion}</dd>
          </div>
          <div>
            <dt>{t("release.storage")}</dt>
            <dd>{t("release.storageValue")}</dd>
          </div>
        </dl>
      </section>

      <footer className="start-footer">
        <p>{t("footer.disclaimer")}</p>
        <a href="/sample">{t("footer.sampleCta")}</a>
      </footer>

      {installOpen && (
        <div
          className="start-dialog-backdrop"
          role="presentation"
          onMouseDown={closeOnBackdrop}
        >
          <section
            className="start-install-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mac-install-title"
          >
            <header>
              <div>
                <p>{t("install.eyebrow", { channel: releaseChannel })}</p>
                <h2 id="mac-install-title">{t("install.title")}</h2>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                aria-label={t("install.closeLabel")}
                onClick={() => setInstallOpen(false)}
              >
                <X aria-hidden="true" />
              </button>
            </header>

            <div className="start-install-release">
              <img src="/favicon.svg" alt="" />
              <div>
                <strong>
                  {t("brand.name")} v{MAC_RELEASE.version}
                </strong>
                <span>
                  {MAC_RELEASE.architecture} · {MAC_RELEASE.minimumSystemVersion}+
                </span>
              </div>
              <span className="start-preview-badge">
                {MAC_RELEASE.notarized
                  ? t("install.stableBadge")
                  : t("install.previewBadge")}
              </span>
            </div>

            {MAC_RELEASE.notarized ? (
              <>
                <ol className="start-install-steps">
                  <li>
                    <span>1</span>
                    <div>
                      <strong>{t("install.stableStep1Title")}</strong>
                      <p>{t("install.stableStep1Body")}</p>
                    </div>
                  </li>
                  <li>
                    <span>2</span>
                    <div>
                      <strong>{t("install.stableStep2Title")}</strong>
                      <p>{t("install.stableStep2Body")}</p>
                    </div>
                  </li>
                  <li>
                    <span>3</span>
                    <div>
                      <strong>{t("install.stableStep3Title")}</strong>
                      <p>{t("install.stableStep3Body")}</p>
                    </div>
                  </li>
                </ol>
                <aside className="start-install-warning">
                  <ShieldCheck aria-hidden="true" />
                  <p>{t("install.stableWarning")}</p>
                </aside>
              </>
            ) : (
              <>
                <ol className="start-install-steps">
                  <li>
                    <span>1</span>
                    <div>
                      <strong>{t("install.step1Title")}</strong>
                      <p>{t("install.step1Body")}</p>
                    </div>
                  </li>
                  <li>
                    <span>2</span>
                    <div>
                      <strong>{t("install.step2Title")}</strong>
                      <p>{t("install.step2Body")}</p>
                    </div>
                  </li>
                  <li>
                    <span>3</span>
                    <div>
                      <strong>{t("install.step3Title")}</strong>
                      <p>{t("install.step3Body")}</p>
                    </div>
                  </li>
                </ol>
                <figure className="start-install-screenshot">
                  <a
                    href="/images/macos-open-anyway-ko.png"
                    target="_blank"
                    rel="noreferrer"
                    aria-label={t("install.screenshotLinkLabel")}
                  >
                    <img
                      src="/images/macos-open-anyway-ko.png"
                      width="1440"
                      height="456"
                      loading="lazy"
                      alt={t("install.screenshotAlt")}
                    />
                  </a>
                  <figcaption>
                    {t("install.screenshotCaptionPrefix")}{" "}
                    <strong>{t("install.screenshotButtonName")}</strong>
                    {t("install.screenshotCaptionSuffix")}
                  </figcaption>
                </figure>
                <aside className="start-install-warning">
                  <ShieldCheck aria-hidden="true" />
                  <p>
                    {t("install.warningPrefix")}{" "}
                    <a
                      href={APPLE_OPEN_ANYWAY_URL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("install.appleGuide")}
                      <ExternalLink aria-hidden="true" />
                    </a>
                  </p>
                </aside>
              </>
            )}

            <div className="start-install-actions">
              {releaseReady ? (
                <a
                  className="start-download-button"
                  href={releaseDownloadUrl}
                  onClick={() => {
                    void recordDownloadIntent(MAC_RELEASE.version);
                  }}
                >
                  <Download aria-hidden="true" />
                  {t("install.download")}
                </a>
              ) : (
                <button
                  className="start-download-button"
                  type="button"
                  disabled
                >
                  <Download aria-hidden="true" />
                  {t("install.verifying")}
                </button>
              )}
              <button
                className="start-web-fallback"
                type="button"
                onClick={() => {
                  window.location.href = "/farm";
                }}
              >
                {t("install.webFallback")}
              </button>
            </div>

            <div className="start-checksum">
              <span>SHA-256</span>
              <code>{MAC_RELEASE.sha256}</code>
              <button
                type="button"
                onClick={copyChecksum}
                disabled={!releaseReady}
              >
                {copied ? (
                  <Check aria-label={t("install.checksumCopied")} />
                ) : (
                  <Copy aria-label={t("install.copyChecksum")} />
                )}
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
